import React, { FC } from 'react';
import { PermissionsGate } from '@kontentino/react-permissions-gate';
import { UserPermission } from 'constants/userPermission';
import { Button, Icon } from '@kontentino/ui';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import PageTypeUtils from 'app/utils/pageType';
import PostTypeUtils from 'app/utils/postType';
import { faShareAll } from '@fortawesome/pro-regular-svg-icons/faShareAll';
import useBoolean from 'utils/hooks/useBoolean';
import PostOverviewLocalPostsSchedulePosts from 'app/modules/posts/modules/post/components/overview/postOverviewLocalPosts/PostOverviewLocalPostsSchedulePosts';
import { ListPost, PostDetail } from 'types/PostDetail';
import { useMutation, useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { queryClient } from 'api/client';
import PostApi from 'api/post';
import { useToast } from 'app/hooks/useToast';
import { isGlobalPostDistributed } from 'app/modules/posts/utils/globalPosts';
import { useTranslation } from 'react-i18next';
import { useUpdatePostStatus } from '../../../post/hooks/useUpdatePostStatus';
import PageApi from 'api/page';

type Props = {
  post: PostDetail;
};

const GlobalPostScheduleWorkflowButton: FC<Props> = ({ post }) => {
  const [isScheduleLocalPostsOpen, setIsScheduleLocalPostsOpen] =
    useBoolean(false);
  const toast = useToast();
  const { t } = useTranslation();
  const updatePostStatus = useUpdatePostStatus();

  const { data: localPages, isLoading: isLocalPagesLoading } = useQuery(
    queryKey.localPages(post.page.id),
    () => PageApi.localPages(post.page.id),
  );

  const distributePost = async () => {
    updatePostStatus(post.id, STATUS.APPROVED, {
      shouldUpdateComments: false,
    });
    toast(t('globalPages.thePostWasDistributed'), 'success');
  };

  const scheduleMutation = useMutation(
    async (scheduleValues: ListPost[]) => {
      const postIds = scheduleValues.map((selectedPost) => selectedPost.id);
      const validationResponse = await PostApi.validate(postIds);

      if (validationResponse) {
        return PostApi.schedule({
          ids: postIds,
          chosenMethod: 'publish',
        });
      } else {
        throw new Error('Validation failed');
      }
    },
    {
      onSuccess: (data, variables) => {
        toast('The post has been scheduled.', 'success');
        queryClient.invalidateQueries(queryKey.postDetail(post.id));
        setIsScheduleLocalPostsOpen.off();
      },
      onError: (error) => {
        toast(t('somethingWentWrong'), 'error');
      },
    },
  );

  return (
    <>
      <PostOverviewLocalPostsSchedulePosts
        localPosts={post.localPosts}
        isOpen={isScheduleLocalPostsOpen}
        onClose={() => setIsScheduleLocalPostsOpen.off()}
        onSchedule={(scheduleValues) => {
          scheduleMutation.mutate(scheduleValues);
        }}
        isScheduleLoading={scheduleMutation.isLoading}
      />

      {!isGlobalPostDistributed(post) && (
        <PermissionsGate
          scopes={[
            UserPermission.POST_SCHEDULE,
            UserPermission.POST_DISTRIBUTE_GLOBAL_CHANGES,
          ]}
        >
          <Button
            onClick={() => distributePost()}
            iconBefore={<Icon icon={faShareAll} />}
            disabled={
              localPages?.data.length === 0 ||
              post.localPosts?.data?.length === 0
            }
            data-name="posts-preview-distribute"
            data-cy="post-distribute-button"
            data-social-media={PageTypeUtils.getName(post.page.type)}
            data-post-type={PostTypeUtils.getExactName(post.type, post.subtype)}
            isLoading={isLocalPagesLoading}
          >
            {t('globalPages.distribute')}
          </Button>
        </PermissionsGate>
      )}

      <PermissionsGate scopes={UserPermission.POST_SCHEDULE}>
        <Button
          onClick={() => {
            queryClient.invalidateQueries(queryKey.postDetail(post.id));
            setIsScheduleLocalPostsOpen.on();
          }}
          iconBefore={<Icon icon={faClock} />}
          variant="success"
          disabled={post.localPosts?.data?.length === 0}
          data-name="posts-preview-schedule-local-posts"
          data-cy="post-schedule-local-posts-button"
          data-social-media={PageTypeUtils.getName(post.page.type)}
          data-post-type={PostTypeUtils.getExactName(post.type, post.subtype)}
          isLoading={isLocalPagesLoading}
        >
          {t('globalPages.scheduleLocalPosts')}
        </Button>
      </PermissionsGate>
    </>
  );
};

export default GlobalPostScheduleWorkflowButton;
